import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { marketData } from '../api/client';

function DownloadProgress({ taskId, onDone }) {
  const [task, setTask] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!taskId) return;
    const poll = () => {
      marketData.getTask(taskId).then(res => {
        setTask(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          clearInterval(intervalRef.current);
          if (onDone) onDone(res.data);
        }
      }).catch(() => {});
    };
    poll();
    intervalRef.current = setInterval(poll, 2000);
    return () => clearInterval(intervalRef.current);
  }, [taskId, onDone]);

  if (!task) return null;

  return (
    <div className="download-progress-item">
      <div className="download-progress-info">
        <span className="download-progress-symbol">{task.symbol}</span>
        <span className="download-progress-tf">{task.timeframe}</span>
        <span className={`download-progress-status status-${task.status}`}>{task.status}</span>
      </div>
      {task.status === 'running' && (
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${task.progress}%` }} />
        </div>
      )}
      {task.status === 'completed' && task.dataset_id && (
        <div className="download-progress-result">
          {task.bar_count} candles imported —{' '}
          <Link to={`/session/new/${task.dataset_id}`}>Start Replay</Link>
        </div>
      )}
      {task.status === 'failed' && (
        <div className="download-progress-error">{task.error_message}</div>
      )}
    </div>
  );
}

export default function MarketDataPage() {
  const [sources, setSources] = useState([]);
  const [activeSource, setActiveSource] = useState('');
  const [query, setQuery] = useState('');
  const [instruments, setInstruments] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [timeframe, setTimeframe] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTasks, setActiveTasks] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  // Load sources on mount
  useEffect(() => {
    marketData.sources().then(res => {
      setSources(res.data);
      if (res.data.length > 0) {
        setActiveSource(res.data[0].id);
        setTimeframe(res.data[0].timeframes[0] || '');
      }
    });
    // Load recent tasks
    marketData.listTasks().then(res => {
      const running = res.data.filter(t => t.status === 'running' || t.status === 'pending');
      setActiveTasks(running.map(t => t.id));
    });
  }, []);

  // Set default dates (last year)
  useEffect(() => {
    const now = new Date();
    const yearAgo = new Date(now);
    yearAgo.setFullYear(now.getFullYear() - 1);
    setDateTo(now.toISOString().split('T')[0]);
    setDateFrom(yearAgo.toISOString().split('T')[0]);
  }, []);

  const currentSource = sources.find(s => s.id === activeSource);

  // Debounced instrument search
  useEffect(() => {
    if (!activeSource || query.length < 1) {
      setInstruments([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      marketData.searchInstruments(activeSource, query)
        .then(res => setInstruments(res.data.instruments || []))
        .catch(() => setInstruments([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [activeSource, query]);

  // Reset on source change
  useEffect(() => {
    setQuery('');
    setInstruments([]);
    setSelected(null);
    if (currentSource) {
      setTimeframe(currentSource.timeframes[0] || '');
    }
  }, [activeSource, currentSource]);

  const handleDownload = useCallback(async () => {
    if (!selected || !timeframe || !dateFrom || !dateTo) return;
    setError('');
    setDownloading(true);
    try {
      const res = await marketData.startDownload({
        source: activeSource,
        symbol: selected.symbol,
        timeframe,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setActiveTasks(prev => [res.data.task_id, ...prev]);
      setSelected(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  }, [selected, timeframe, dateFrom, dateTo, activeSource]);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Market Data</h1>
        <p className="page-subtitle">Download historical data from external sources</p>
      </div>

      {/* Source Tabs */}
      <div className="source-tabs">
        {sources.map(src => (
          <button
            key={src.id}
            className={`source-tab ${activeSource === src.id ? 'active' : ''}`}
            onClick={() => setActiveSource(src.id)}
          >
            {src.name}
          </button>
        ))}
      </div>

      <div className="market-data-content">
        {/* Search */}
        <div className="card">
          <input
            type="text"
            className="market-search-input"
            placeholder={`Search ${currentSource?.name || ''} instruments...`}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(null); }}
          />

          {/* Results list */}
          {instruments.length > 0 && !selected && (
            <div className="instrument-list">
              {instruments.map((inst, i) => (
                <div
                  key={`${inst.symbol}-${i}`}
                  className="instrument-item"
                  onClick={() => setSelected(inst)}
                >
                  <span className="instrument-symbol">{inst.symbol}</span>
                  <span className="instrument-name">{inst.name}</span>
                  <span className={`instrument-type type-${inst.type}`}>{inst.type}</span>
                </div>
              ))}
            </div>
          )}
          {searching && <div className="search-loading">Searching...</div>}
        </div>

        {/* Config panel */}
        {selected && (
          <div className="card download-config">
            <h3>
              Download {selected.symbol}
              <span className="instrument-name-sub">{selected.name}</span>
            </h3>

            <div className="config-row">
              <div className="config-field">
                <label>Timeframe</label>
                <select value={timeframe} onChange={e => setTimeframe(e.target.value)}>
                  {(currentSource?.timeframes || []).map(tf => (
                    <option key={tf} value={tf}>{tf}</option>
                  ))}
                </select>
              </div>
              <div className="config-field">
                <label>From</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </div>
              <div className="config-field">
                <label>To</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
            </div>

            {error && <div className="download-error">{error}</div>}

            <div className="config-actions">
              <button className="btn" onClick={handleDownload} disabled={downloading}>
                {downloading ? 'Starting...' : 'Download'}
              </button>
              <button className="btn btn-sm" onClick={() => setSelected(null)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Active downloads */}
        {activeTasks.length > 0 && (
          <div className="card">
            <h3>Downloads</h3>
            {activeTasks.map(taskId => (
              <DownloadProgress key={taskId} taskId={taskId} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
